import { useState } from 'react'
import { BlackActionButton } from './buttons/BlackActionButton'
import { ReviewCard } from './ReviewCard'
import { RepliesSection } from './RepliesSection'
import { WriteReviewDialog } from './dialogs/WriteReviewDialog'
import { WriteReplyDialog } from './dialogs/WriteReplyDialog'
import { ReportReviewDialog } from './dialogs/ReportReviewDialog'
import { OkDialog } from './dialogs/OkDialog'
import {
  useReviewsByFragrance,
  useUserLikedReviews,
  useReviewLikesCounts,
  useUserReviewForFragrance,
  useToggleReviewLike,
  type IReviewWithUser,
} from '../queries/useReviews'
import { pb } from '../pocketbase/Pocketbase'
import { useAuthStore } from '../data/useAuthStore'

interface ReviewsSectionProps {
  fragranceId: string
}

export const ReviewsSection = ({ fragranceId }: ReviewsSectionProps) => {
  const { userProfile } = useAuthStore()
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false)
  const [isReplyDialogOpen, setIsReplyDialogOpen] = useState(false)
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false)
  const [isOkDialogOpen, setIsOkDialogOpen] = useState(false)
  const [okDialogMessage, setOkDialogMessage] = useState('')
  const [replyingToReview, setReplyingToReview] = useState<IReviewWithUser | null>(null)
  const [reportingReviewId, setReportingReviewId] = useState<string | null>(null)

  // Queries
  const { data: reviewsData, isLoading: isLoadingReviews } = useReviewsByFragrance(fragranceId)
  const reviews = reviewsData?.items ?? []
  const { data: likedReviewIds = new Set<string>(), isLoading: isLoadingLikes } = useUserLikedReviews()
  const { data: userReview, isLoading: isLoadingUserReview } = useUserReviewForFragrance(fragranceId)

  const reviewIds = reviews.map(r => r.id)
  const { data: likesCounts = new Map<string, number>() } = useReviewLikesCounts(reviewIds)

  const isLoading = isLoadingReviews || isLoadingLikes || isLoadingUserReview
  const hasUserReviewed = !!userReview
  const canWriteReview = pb.authStore.isValid && !hasUserReviewed

  // Mutations
  const toggleLikeMutation = useToggleReviewLike()

  const handleLike = (reviewId: string) => {
    if (!pb.authStore.isValid) {
      setOkDialogMessage('Please log in to like a review')
      setIsOkDialogOpen(true)
      return
    }
    toggleLikeMutation.mutate(reviewId)
  }

  const handleWriteReview = () => {
    if (!pb.authStore.isValid) {
      setOkDialogMessage('Please log in to write a review')
      setIsOkDialogOpen(true)
      return
    }
    setIsReviewDialogOpen(true)
  }

  const handleReply = (review: IReviewWithUser) => {
    if (!pb.authStore.isValid) {
      setOkDialogMessage('Please log in to reply to a review')
      setIsOkDialogOpen(true)
      return
    }
    setReplyingToReview(review)
    setIsReplyDialogOpen(true)
  }

  const handleReport = (reviewId: string) => {
    if (!pb.authStore.isValid) {
      setOkDialogMessage('Please log in to report a review')
      setIsOkDialogOpen(true)
      return
    }
    setReportingReviewId(reviewId)
    setIsReportDialogOpen(true)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 14) return '1 week ago'
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    if (diffDays < 60) return '1 month ago'
    return `${Math.floor(diffDays / 30)} months ago`
  }

  return (
    <>
      <div className={'mb-10'}>
        {/* Reviews Header */}
        <div className={'flex items-center justify-between mb-6'}>
          <h2 className={'md:text-3xl text-2xl text-title'}>Reviews</h2>
          {!isLoading && canWriteReview && reviews.length > 0 && (
            <BlackActionButton label={'Write a review'} onClick={handleWriteReview} />
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center text-gray-500 py-8">
            Loading reviews...
          </div>
        )}

        {/* Empty State */}
        {!isLoading && reviews.length === 0 && (
          <div className="text-center text-gray-500 py-12 bg-white">
            <p className="text-2xl text-title mb-2">No reviews yet</p>
            <p className="text-sm">Be the first to share your experience with this fragrance!</p>
            {canWriteReview && (
              <div className="mt-6">
                <BlackActionButton label={'Write a review'} onClick={handleWriteReview} />
              </div>
            )}
          </div>
        )}

        {/* Review Cards */}
        {!isLoading && reviews.map((review) => (
          <div key={review.id}>
            <ReviewCard
              username={review.username}
              rating={review.rating}
              reviewText={review.text}
              date={formatDate(review.created)}
              likesCount={likesCounts.get(review.id) ?? 0}
              isLiked={likedReviewIds.has(review.id)}
              isOwnReview={review.userId === userProfile?.id}
              onLike={() => handleLike(review.id)}
              onReply={() => handleReply(review)}
              onReport={() => handleReport(review.id)}
            />
            <RepliesSection reviewId={review.id} />
          </div>
        ))}
      </div>

      <WriteReviewDialog
        isOpen={isReviewDialogOpen}
        onClose={() => setIsReviewDialogOpen(false)}
        fragranceId={fragranceId}
      />

      <WriteReplyDialog
        isOpen={isReplyDialogOpen}
        onClose={() => {
          setIsReplyDialogOpen(false)
          setReplyingToReview(null)
        }}
        reviewId={replyingToReview?.id ?? null}
        reviewUsername={replyingToReview?.username}
      />

      <ReportReviewDialog
        isOpen={isReportDialogOpen}
        onClose={() => {
          setIsReportDialogOpen(false)
          setReportingReviewId(null)
        }}
        reviewId={reportingReviewId}
      />

      <OkDialog
        isOpen={isOkDialogOpen}
        onClose={() => setIsOkDialogOpen(false)}
        title="Login Required"
        content={okDialogMessage}
        okLabel="OK"
      />
    </>
  )
}
